import { isIntegrationConfigured } from './integrationConfigured'
import { METRICS_SOURCE_KEYS } from './features/rightsizing'

const item = (id, label, path, icon, options = {}) => ({ id, label, path, icon, ...options })

export const INTEGRATIONS_SECTION = { id: 'integrations', label: 'Integrations' }

/** Every integration page, in Integrations section order. The section lists a page once it is
 * permitted and set up; before that, Settings → Integrations still links the page, and the header
 * names it from here rather than from its URL. */
export const INTEGRATION_DESTINATIONS = [
  item('checkmk', 'CheckMK', '/checkmk', 'monitor'),
  item('kubernetes', 'Kubernetes', '/kubernetes-clusters', 'globe'),
  item('git-repositories', 'Git Repositories', '/git-repositories', 'code'),
  item('wazuh', 'Wazuh', '/wazuh', 'shield'),
  item('velociraptor', 'Velociraptor', '/velociraptor', 'shield'),
  item('microsoft365', 'Microsoft 365', '/microsoft-365', 'cloud'),
  item('storagegrid', 'StorageGRID', '/storagegrid', 'cloud'),
  item('scaleway', 'Scaleway', '/scaleway', 'cloud'),
  item('cloudflare', 'Cloudflare', '/cloudflare', 'cloud'),
  item('vsphere', 'vSphere', '/vsphere', 'cloud'),
  item('hyperv', 'Hyper-V', '/hyperv', 'cloud'),
  item('netscaler', 'NetScaler', '/netscaler', 'cloud'),
  item('argoworkflows', 'Argo Workflows', '/argoworkflows', 'cloud'),
  item('argocd', 'Argo CD', '/argocd', 'cloud'),
  item('salt', 'Salt', '/salt', 'cloud'),
  item('azure', 'Azure', '/azure', 'cloud'),
  item('datadog', 'Datadog', '/datadog', 'monitor'),
]

const INTEGRATION_DESTINATION_BY_ID = Object.fromEntries(INTEGRATION_DESTINATIONS.map((destination) => [destination.id, destination]))

/** Canonical navigation eligibility, shared by the console menus and the navigation assistant's destination list.
 * @param {import('./uiNavigation').UiNavigationPolicy} policy
 */
export function buildAppNavigation({ canRead, isAdmin, isGlobalAdmin, isFeatureEnabled, showLicensePage = false, integrationSummary = {}, integrationSummaryError = null, integrationSummaryLoading = false, permissionLoading = false, entitlementLoading = false }) {
  const canReadLicensed = (section, feature) => canRead(section) && isFeatureEnabled(feature)
  // One shared signal for "the customer has this set up", keyed by entitlement
  // key, replacing four per-integration count reads. Permission and
  // configuration are now independent gates rather than one standing in for
  // the other: a configured integration the caller cannot read is hidden by
  // canRead, not by its count coming back zero.
  const configured = (entitlementKey) => isIntegrationConfigured(integrationSummary, entitlementKey)
  const integrationCount = (key) => (
    key === 'git'
      ? integrationSummary.git?.active || 0
      : integrationSummary[key]?.count || 0
  )

  // A listed integration page, counted under countKey in the integration summary (null: no count).
  const integrationItem = (id, countKey = id) => (
    countKey
      ? { ...INTEGRATION_DESTINATION_BY_ID[id], count: integrationCount(countKey) }
      : { ...INTEGRATION_DESTINATION_BY_ID[id] }
  )

  const canAlerts = canReadLicensed('Alerts', 'alerts')
  const canInvestigate = canReadLicensed('CheckMK', 'investigate')
  const baseCanKubernetes = canReadLicensed('Kubernetes', 'kubernetes')
  const baseCanGitRepos = isGlobalAdmin() && isFeatureEnabled('git_repositories')
  const canCheckmk = canInvestigate && configured('checkmk')
  const canKubernetes = baseCanKubernetes && configured('kubernetes')
  const canGitRepos = baseCanGitRepos && configured('git_repositories')
  const canWazuh = canReadLicensed('Wazuh', 'wazuh_integration') && configured('wazuh_integration')
  // Microsoft 365 is available as soon as the integration is enabled.
  const canMicrosoft365 = canReadLicensed('Settings', 'msgraph')
  const canStorageGrid = canReadLicensed('Settings', 'storagegrid') && configured('storagegrid')
  const canScaleway = canReadLicensed('Settings', 'scaleway') && configured('scaleway')
  const canCloudflare = canReadLicensed('Settings', 'cloudflare') && configured('cloudflare')
  const canVsphere = canReadLicensed('Settings', 'vsphere') && configured('vsphere')
  const canHyperv = canReadLicensed('Settings', 'hyperv') && configured('hyperv')
  const canNetscaler = canReadLicensed('Settings', 'netscaler') && configured('netscaler')
  const canArgoWorkflows = canReadLicensed('Settings', 'argoworkflows') && configured('argoworkflows')
  const canArgoCD = canReadLicensed('Settings', 'argocd') && configured('argocd')
  const canSalt = canReadLicensed('Settings', 'salt') && configured('salt')
  const canAzure = canReadLicensed('Settings', 'azure') && configured('azure')
  const canDatadog = canReadLicensed('Settings', 'datadog') && configured('datadog')
  const canVelociraptor = canReadLicensed('ApplianceConfig', 'appliance_app_velociraptor') && configured('appliance_app_velociraptor')
  const canRoutines = canRead('Alerts') && isFeatureEnabled('routines')
  const canVulnerabilityManagement = canRead('SecurityAppliances')
    && (isFeatureEnabled('vulnerability_scanning') || isFeatureEnabled('vulnerability_reporting'))
  const canHardening = canReadLicensed('SecurityAppliances', 'hardening')
  const canZeroTrust = canRead('VPCs') && isFeatureEnabled('cloudflare') && configured('cloudflare')
  // Rightsizing has nothing to analyse until a metrics source is set up, so
  // it follows the same licensed-AND-configured rule as every other
  // integration-backed entry. Asked of the shared source list rather than of
  // 'checkmk' by name: adding Zabbix or Datadog later must not require
  // editing the navigation.
  const canRightsizing = canReadLicensed('Rightsizing', 'rightsizing')
    && METRICS_SOURCE_KEYS.some((key) => configured(key))
  const hasExecutionDashboardAccess =
    canReadLicensed('Agents', 'agents') ||
    canReadLicensed('Playbooks', 'playbooks') ||
    canReadLicensed('Schedules', 'schedules') ||
    canReadLicensed('Queue', 'queue')
  const hasAppliancesAccess = canReadLicensed('Agents', 'agents')
  const hasLicensedIntegrationsAccess = canInvestigate || baseCanKubernetes || baseCanGitRepos || canRead('Wazuh') || canMicrosoft365 || canStorageGrid || canArgoCD || canScaleway || canVsphere || canHyperv || canNetscaler || canAzure || canDatadog

  const sections = [
    {
      id: 'operations',
      label: 'Operations',
      homePath: canAlerts
        ? '/dashboard/alerts'
        : canInvestigate
          ? '/investigate'
          : canRoutines
            ? '/routines'
            : '/ai-governor',
      items: [
        canAlerts && item('alerts', 'Alerts', '/alerts', 'bell'),
        canInvestigate && item('investigate', 'Investigate', '/investigate', 'search', {
          activePrefixes: ['/investigate', '/cockpit'],
          children: [
            item('investigate-workspace', 'Workspace', '/investigate'),
            item('investigate-history', 'History', '/investigate/history'),
          ],
        }),
        canRoutines && item('routines', 'Routines', '/routines', 'clock', { activePrefixes: ['/routines'] }),
        canRead('Settings') && item('ai-governor', 'AI Governor', '/ai-governor', 'shield'),
      ].filter(Boolean),
    },
    {
      id: 'security',
      label: 'Security',
      homePath: (canVulnerabilityManagement || canHardening) ? '/dashboard/security' : '/security/zero-trust',
      items: [
        canVulnerabilityManagement && item(
          'vulnerability-management',
          'Vulnerability Management',
          '/security/vulnerability-management',
          'shield',
          { activePrefixes: ['/security/vulnerability-management'] },
        ),
        canHardening && item('hardening', 'Hardening', '/security/hardening', 'lock', { activePrefixes: ['/security/hardening'] }),
        canZeroTrust && item('zero-trust', 'Zero Trust', '/security/zero-trust', 'shield'),
      ].filter(Boolean),
    },
    {
      id: 'compliance',
      label: 'Compliance',
      items: [
        canReadLicensed('NIS2', 'nis2') && item('nis2', 'NIS2', '/compliance/nis2', 'notes'),
        canReadLicensed('ISO27001', 'iso27001') && item('iso27001', 'ISO 27001', '/compliance/iso27001', 'lock'),
        canReadLicensed('ComplianceCalendar', 'compliance_calendar') && item('compliance-calendar', 'Calendar', '/compliance/calendar', 'calendar'),
        canReadLicensed('Docs', 'docs') && item('docs', 'Documentation', '/docs', 'book'),
      ].filter(Boolean),
    },
    {
      id: 'execution',
      label: 'Execution',
      homePath: hasExecutionDashboardAccess ? '/dashboard/execution' : null,
      items: [
        (canReadLicensed('Agents', 'agents') || canReadLicensed('Queue', 'queue')) && item('agents-group', 'Agents', '/agents', 'terminal', {
          activePrefixes: ['/agents', '/queue'],
          children: [
            canReadLicensed('Agents', 'agents') && item('agents', 'Agents', '/agents'),
            canReadLicensed('Queue', 'queue') && item('queue', 'Queue', '/queue'),
          ].filter(Boolean),
        }),
        canReadLicensed('Playbooks', 'playbooks') && item('playbooks', 'Playbooks', '/playbooks', 'code'),
        item('jit', 'JIT', '/jit-access', 'lock', {
          activePrefixes: ['/jit-access', '/jit-admin'],
          children: [
            item('jit-access', 'JIT Access', '/jit-access'),
            (isGlobalAdmin() || isAdmin('JIT')) && item('jit-admin', 'JIT Admin', '/jit-admin'),
          ].filter(Boolean),
        }),
        canReadLicensed('Schedules', 'schedules') && item('schedules', 'Schedules', '/schedules', 'calendar'),
        canReadLicensed('Calendar', 'calendar') && item('calendar', 'Calendar', '/calendar', 'clock'),
        canReadLicensed('Calendar', 'calendar') && item('calendar-v2', 'Calendar V2', '/calendar/v2', 'calendar'),
      ].filter(Boolean),
    },
    {
      id: 'inventory',
      label: 'Inventory',
      items: [
        hasAppliancesAccess && item('appliances', 'Appliances', '/appliances', 'monitor', { activePrefixes: ['/appliances'] }),
        canReadLicensed('AssetOwnership', 'asset_ownership') && item('asset-ownership', 'Asset Ownership', '/asset-ownership', 'task'),
        canRightsizing && item('rightsizing', 'Rightsizing', '/rightsizing', 'chart'),
      ].filter(Boolean),
    },
    {
      ...INTEGRATIONS_SECTION,
      items: [
        Boolean(integrationSummaryError) && hasLicensedIntegrationsAccess && item('integrations-unavailable', 'Status unavailable', null, 'warning', { unavailable: true }),
        canCheckmk && integrationItem('checkmk'),
        canKubernetes && integrationItem('kubernetes'),
        canGitRepos && integrationItem('git-repositories', 'git'),
        canWazuh && integrationItem('wazuh'),
        canVelociraptor && integrationItem('velociraptor'),
        canMicrosoft365 && integrationItem('microsoft365', null),
        canStorageGrid && integrationItem('storagegrid'),
        canScaleway && integrationItem('scaleway'),
        canCloudflare && integrationItem('cloudflare'),
        canVsphere && integrationItem('vsphere'),
        canHyperv && integrationItem('hyperv'),
        canNetscaler && integrationItem('netscaler'),
        canArgoWorkflows && integrationItem('argoworkflows'),
        canArgoCD && integrationItem('argocd'),
        canSalt && integrationItem('salt'),
        canAzure && integrationItem('azure'),
        canDatadog && integrationItem('datadog'),
      ].filter(Boolean),
    },
    {
      id: 'system',
      label: 'System',
      items: [
        canAlerts && item('usage', 'AI Value & Usage', '/usage', 'chart'),
        isAdmin('Users') && isFeatureEnabled('users') && item('users', 'Users', '/users', 'people'),
        isGlobalAdmin() && isFeatureEnabled('settings') && item('settings', 'Settings', '/settings', 'settings'),
        isGlobalAdmin() && showLicensePage && item('license', 'License', '/license', 'description'),
        isGlobalAdmin() && item('onboarding', 'Onboarding', '/onboarding', 'dashboard'),
      ].filter(Boolean),
    },
  ].filter((section) => section.items.length > 0)

  return {
    loading: permissionLoading || entitlementLoading || integrationSummaryLoading,
    sections,
  }
}
